import React, { useState, useEffect } from 'react';
import { Home, Stethoscope, Sparkles, Users, HelpCircle, Mail, Calendar } from 'lucide-react';
import { LimelightNav } from '../components/ui/limelight-nav';
import HeroSection from '../components/sections/Hero';
import Services from '../components/sections/Services';
import Specialites from '../components/sections/Specialites';
import Technologies from '../components/sections/Technologies';
import Equipe from '../components/sections/Equipe';
import FAQ from '../components/sections/FAQ';
import Galerie from '../components/sections/Galerie';
import Contact from '../components/sections/Contact';
import Footer from '../components/sections/Footer';
import { cabinetInfo } from '../mock';

const sections = ['accueil', 'services', 'specialites', 'technologies', 'equipe', 'faq', 'galerie', 'contact'];

const NAV_ITEMS = [
  { id: 'accueil', section: 'accueil', icon: <Home />, label: 'Accueil' },
  { id: 'services', section: 'services', icon: <Stethoscope />, label: 'Soins' },
  { id: 'specialites', section: 'specialites', icon: <Sparkles />, label: 'Spécialités' },
  { id: 'equipe', section: 'equipe', icon: <Users />, label: 'Équipe' },
  { id: 'faq', section: 'faq', icon: <HelpCircle />, label: 'FAQ' },
  { id: 'contact', section: 'contact', icon: <Mail />, label: 'Contact' },
];

const computeOpenNow = () => {
  const now = new Date();
  const day = now.getDay();
  if (day === 0 || day === 6) return false;
  const m = now.getHours() * 60 + now.getMinutes();
  const closeAft = (day === 3 || day === 5) ? 18 * 60 : 19 * 60;
  return (m >= 9 * 60 && m < 12 * 60 + 30) || (m >= 14 * 60 && m < closeAft);
};

export default function HomePage() {
  const [activeSection, setActiveSection] = useState('accueil');
  const openNow = computeOpenNow();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && scrollPosition >= el.offsetTop) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({
      top: el.offsetTop - 80,
      behavior: 'smooth',
    });
  };

  // Sections without their own nav item (technologies, galerie) borrow the nearest tab so the light bar never lands on emptiness.
  const activeIndex = (() => {
    const exact = NAV_ITEMS.findIndex((i) => i.section === activeSection);
    if (exact !== -1) return exact;
    if (activeSection === 'technologies') return NAV_ITEMS.findIndex((i) => i.section === 'specialites');
    if (activeSection === 'galerie') return NAV_ITEMS.findIndex((i) => i.section === 'equipe');
    return 0;
  })();

  const navItems = NAV_ITEMS.map((item) => ({
    id: item.id,
    icon: item.icon,
    label: item.label,
    onClick: () => scrollToSection(item.section),
  }));

  return (
    <div className="min-h-screen bg-background text-foreground antialiased font-sans">
      <div className="fixed top-4 sm:top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 sm:gap-3">
        <LimelightNav
          items={navItems}
          activeIndex={activeIndex >= 0 ? activeIndex : 0}
          className="!h-12 sm:!h-14 !rounded-full !px-1.5 bg-background/85 backdrop-blur-md !border-foreground/10 shadow-xl shadow-foreground/5"
          iconContainerClassName="!p-2.5 sm:!p-3.5"
          iconClassName="!w-5 !h-5 text-foreground"
          limelightClassName="!w-9 bg-amber-400 shadow-[0_30px_18px_rgba(251,191,36,0.55)] text-amber-400"
        />

        <a
          href={cabinetInfo.doctolib}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative inline-flex items-center justify-center gap-2 h-12 sm:h-14 px-4 sm:px-5 rounded-full bg-foreground text-background text-xs font-semibold uppercase tracking-[0.2em] shadow-xl shadow-foreground/10 hover:bg-amber-500 hover:text-foreground hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 transition-all"
          aria-label="Prendre rendez-vous en ligne"
        >
          <Calendar className="w-4 h-4" aria-hidden="true" />
          <span className="hidden sm:inline">Rendez-vous</span>
        </a>
      </div>

      <main>
        <HeroSection openNow={openNow} scrollToSection={scrollToSection} />
        <Services scrollToSection={scrollToSection} />
        <Specialites scrollToSection={scrollToSection} />
        <Technologies />
        <Equipe />
        <FAQ />
        <Galerie />
        <Contact />
      </main>
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
}
